import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import {
  findMaterialSkuByCode,
  getSkuDirectoryCategories,
  materialSkuDirectory,
  normalizeMaterialSkuRecord,
} from "../data/materialSkuDirectory";

const MATERIAL_CATEGORY_OPTIONS = [
  "Cement",
  "Steel",
  "Bricks",
  "Sand",
  "Tiles",
  "Electrical",
  "Plumbing",
  "Paint",
  "Hardware",
  "Wood",
  "Glass",
  "Sanitary",
  "General",
];

function ShopOwnerDashboard() {
  const { user, profile, loading: authLoading } = useAuth();

  const [shopOwner, setShopOwner] = useState(null);
  const [shop, setShop] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [catalogues, setCatalogues] = useState([]);
  const [materialOrders, setMaterialOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skuSearch, setSkuSearch] = useState("");
  const [remoteSkuResults, setRemoteSkuResults] = useState([]);
  const [remoteSkuLoading, setRemoteSkuLoading] = useState(false);
  const [remoteSkuError, setRemoteSkuError] = useState("");

  const [shopForm, setShopForm] = useState({
    business_name: "",
    shop_name: "",
    gst_number: "",
    flat_house_no: "",
    street_area: "",
    address: "",
    city: "Hyderabad",
    state: "Telangana",
    country: "India",
    pincode: "",
  });

  const [materialForm, setMaterialForm] = useState({
    master_sku_id: "",
    master_sku_code: "",
    material_name: "",
    category: "General",
    quantity: "",
    unit: "unit",
    mrp_price: "",
    offered_price: "",
    image_url: "",
  });

  const [materialImageFile, setMaterialImageFile] = useState(null);
  const [catalogueFile, setCatalogueFile] = useState(null);
  const starterSkuCategories = getSkuDirectoryCategories();
  const starterSkuResults = materialSkuDirectory.filter((item) => {
    if (!skuSearch.trim()) return true;

    const query = skuSearch.trim().toLowerCase();
    const haystack = [
      item.skuCode,
      item.title,
      item.category,
      ...(item.aliases || []),
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(query);
  });
  const displayedSkuResults =
    remoteSkuResults.length > 0
      ? remoteSkuResults
      : starterSkuResults.map(normalizeMaterialSkuRecord);

  useEffect(() => {
    if (authLoading) return;
    if (!user || profile?.role !== "shop_owner") {
      setLoading(false);
      return;
    }

    fetchShopOwnerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, profile?.role]);

  useEffect(() => {
    let active = true;

    const loadRemoteSkus = async () => {
      const query = skuSearch.trim();

      if (!query) {
        setRemoteSkuResults([]);
        setRemoteSkuError("");
        setRemoteSkuLoading(false);
        return;
      }

      try {
        setRemoteSkuLoading(true);
        setRemoteSkuError("");

        const { data, error } = await supabase
          .from("material_master_skus")
          .select("id, sku_code, title, category, unit, aliases, subcategory, brand, size_label")
          .eq("is_active", true)
          .or(
            `sku_code.ilike.%${query}%,title.ilike.%${query}%,category.ilike.%${query}%,subcategory.ilike.%${query}%`
          )
          .order("updated_at", { ascending: false })
          .limit(12);

        if (error) throw error;
        if (!active) return;

        setRemoteSkuResults((data || []).map(normalizeMaterialSkuRecord));
      } catch (error) {
        if (!active) return;
        console.error("SKU search error:", error);
        setRemoteSkuResults([]);
        setRemoteSkuError(error.message || "SKU search failed.");
      } finally {
        if (active) {
          setRemoteSkuLoading(false);
        }
      }
    };

    const timeoutId = setTimeout(loadRemoteSkus, 250);

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [skuSearch]);

  const fetchShopOwnerData = async () => {
    try {
      setLoading(true);

      const { data: shopOwnerData, error: shopOwnerError } = await supabase
        .from("shop_owners")
        .select("*")
        .eq("profile_id", user.id)
        .maybeSingle();

      if (shopOwnerError) throw shopOwnerError;

      let finalShopOwner = shopOwnerData;

      if (!finalShopOwner) {
        const { data: insertedOwner, error: insertOwnerError } = await supabase
          .from("shop_owners")
          .insert([
            {
              profile_id: user.id,
              business_name: "",
              wallet_balance: 0,
              referral_bonus_earned: 0,
            },
          ])
          .select()
          .single();

        if (insertOwnerError) throw insertOwnerError;
        finalShopOwner = insertedOwner;
      }

      setShopOwner(finalShopOwner);

      const { data: shopData, error: shopError } = await supabase
        .from("shops")
        .select("*")
        .eq("shop_owner_id", finalShopOwner.id)
        .maybeSingle();

      if (shopError) throw shopError;

      let finalShop = shopData;

      if (!finalShop) {
        const { data: insertedShop, error: insertShopError } = await supabase
          .from("shops")
          .insert([
            {
              shop_owner_id: finalShopOwner.id,
              shop_name: finalShopOwner.business_name || "My Shop",
              city: "Hyderabad",
              state: "Telangana",
              country: "India",
            },
          ])
          .select()
          .single();

        if (insertShopError) throw insertShopError;
        finalShop = insertedShop;
      }

      setShop(finalShop);

      setShopForm({
        business_name: finalShopOwner.business_name || "",
        shop_name: finalShop.shop_name || "",
        gst_number: finalShop.gst_number || finalShopOwner.gst_number || "",
        flat_house_no: finalShop.flat_house_no || "",
        street_area: finalShop.street_area || "",
        address: finalShop.address || "",
        city: finalShop.city || "Hyderabad",
        state: finalShop.state || "Telangana",
        country: finalShop.country || "India",
        pincode: finalShop.pincode || "",
      });

      const { data: materialsData, error: materialsError } = await supabase
        .from("materials")
        .select("*")
        .eq("shop_id", finalShop.id)
        .order("created_at", { ascending: false });

      if (materialsError) throw materialsError;

      const materialsWithImages = await Promise.all(
        (materialsData || []).map(async (item) => {
          let resolvedImageUrl = item.image_url || "";

          if (!resolvedImageUrl && item.image_file_path) {
            const { data: publicUrlData } = supabase.storage
              .from("shop-assets")
              .getPublicUrl(item.image_file_path);

            resolvedImageUrl = publicUrlData?.publicUrl || "";
          }

          return {
            ...item,
            resolvedImageUrl,
          };
        })
      );

      setMaterials(materialsWithImages);

      const { data: catalogueData, error: catalogueError } = await supabase
        .from("shop_catalogues")
        .select("*")
        .eq("shop_id", finalShop.id)
        .order("created_at", { ascending: false });

      if (catalogueError) throw catalogueError;
      setCatalogues(catalogueData || []);

      const { data: materialOrdersData, error: materialOrdersError } =
        await supabase
          .from("material_orders")
          .select("*")
          .or(`shop_id.eq.${finalShop.id},assigned_shop_id.eq.${finalShopOwner.id}`)
          .order("created_at", { ascending: false });

      if (materialOrdersError) throw materialOrdersError;

      const orderIds = (materialOrdersData || [])
        .map((item) => item.order_id)
        .filter(Boolean);

      let orderAddressMap = {};
      let orderCodeMap = {};

      if (orderIds.length > 0) {
        const { data: orderRows, error: orderRowsError } = await supabase
          .from("orders")
          .select(`
            id,
            order_code,
            addresses (
              house_flat_no,
              street_area,
              city,
              state,
              country,
              pincode
            )
          `)
          .in("id", orderIds);

        if (orderRowsError) throw orderRowsError;

        orderAddressMap = (orderRows || []).reduce((acc, orderRow) => {
          const address = orderRow.addresses;
          acc[orderRow.id] = [
            address?.house_flat_no,
            address?.street_area,
            address?.city,
            address?.state,
            address?.country,
            address?.pincode,
          ]
            .filter(Boolean)
            .join(", ");
          return acc;
        }, {});

        orderCodeMap = (orderRows || []).reduce((acc, orderRow) => {
          acc[orderRow.id] = orderRow.order_code || orderRow.id;
          return acc;
        }, {});
      }

      setMaterialOrders(
        (materialOrdersData || []).map((item) => ({
          ...item,
          orderCode: orderCodeMap[item.order_id] || item.order_id || "N/A",
          deliveryAddressText:
            item.delivery_address_text || orderAddressMap[item.order_id] || "",
        }))
      );
    } catch (error) {
      console.error("Shop owner dashboard error:", error);
      alert(error.message || "Failed to load shop owner dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const handleShopChange = (e) => {
    setShopForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleMaterialChange = (e) => {
    setMaterialForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const applyMasterSku = (skuCode) => {
    const sku =
      remoteSkuResults.find((item) => item.skuCode === skuCode) ||
      findMaterialSkuByCode(skuCode);
    if (!sku) return;

    setMaterialForm((prev) => ({
      ...prev,
      master_sku_id: sku.id || sku.skuCode,
      master_sku_code: sku.skuCode,
      material_name: sku.title,
      category: sku.category,
      unit: sku.unit,
    }));
  };

  const handleMaterialImageChange = (e) => {
    const file = e.target.files?.[0] || null;
    setMaterialImageFile(file);
  };

  const handleCatalogueChange = (e) => {
    const file = e.target.files?.[0] || null;
    setCatalogueFile(file);
  };

  const handleSaveShop = async () => {
    try {
      if (!shopOwner || !shop) return;

      const { error: ownerError } = await supabase
        .from("shop_owners")
        .update({
          business_name: shopForm.business_name,
          gst_number: shopForm.gst_number,
          flat_house_no: shopForm.flat_house_no,
          street_area: shopForm.street_area,
          address: shopForm.address,
          city: shopForm.city,
          state: shopForm.state,
          country: shopForm.country,
          pincode: shopForm.pincode,
        })
        .eq("id", shopOwner.id);

      if (ownerError) throw ownerError;

      const { error: shopError } = await supabase
        .from("shops")
        .update({
          shop_name: shopForm.shop_name,
          gst_number: shopForm.gst_number,
          flat_house_no: shopForm.flat_house_no,
          street_area: shopForm.street_area,
          address: shopForm.address,
          city: shopForm.city,
          state: shopForm.state,
          country: shopForm.country,
          pincode: shopForm.pincode,
        })
        .eq("id", shop.id);

      if (shopError) throw shopError;

      alert("Shop details updated.");
      fetchShopOwnerData();
    } catch (error) {
      console.error("Save shop error:", error);
      alert(error.message || "Failed to save shop details.");
    }
  };

  const handleAddMaterial = async () => {
    try {
      if (!shop) return;

      if (!materialForm.master_sku_code || !materialForm.master_sku_id) {
        alert("Please select a platform master SKU before adding inventory.");
        return;
      }

      if (
        !materialForm.material_name ||
        !materialForm.quantity ||
        !materialForm.mrp_price ||
        !materialForm.offered_price
      ) {
        alert("Please select a SKU and fill quantity, MRP, and offered price.");
        return;
      }

      if (Number(materialForm.offered_price) > Number(materialForm.mrp_price)) {
        alert("Offered price cannot be greater than MRP.");
        return;
      }

      let uploadedImagePath = null;

      if (materialImageFile) {
        const imageFileName = `${shop.id}/materials/${Date.now()}-${materialImageFile.name}`;

        const { error: uploadError } = await supabase.storage
          .from("shop-assets")
          .upload(imageFileName, materialImageFile, {
            upsert: true,
          });

        if (uploadError) throw uploadError;
        uploadedImagePath = imageFileName;
      }

      const { error } = await supabase.from("materials").insert([
        {
          shop_id: shop.id,
          master_sku_id: materialForm.master_sku_id || null,
          master_sku_code: materialForm.master_sku_code || null,
          material_name: materialForm.material_name,
          category: materialForm.category,
          quantity: Number(materialForm.quantity),
          stock_quantity: Math.floor(Number(materialForm.quantity)),
          unit: materialForm.unit,
          mrp_price: Number(materialForm.mrp_price),
          offered_price: Number(materialForm.offered_price),
          price: Number(materialForm.offered_price),
          image_url: materialForm.image_url || null,
          image_file_path: uploadedImagePath,
        },
      ]);

      if (error) throw error;

      setMaterialForm({
        master_sku_id: "",
        master_sku_code: "",
        material_name: "",
        category: "General",
        quantity: "",
        unit: "unit",
        mrp_price: "",
        offered_price: "",
        image_url: "",
      });
      setMaterialImageFile(null);

      alert("Material added successfully.");
      fetchShopOwnerData();
    } catch (error) {
      console.error("Add material error:", error);
      alert(error.message || "Failed to add material.");
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      const { error } = await supabase
        .from("materials")
        .delete()
        .eq("id", materialId);

      if (error) throw error;

      fetchShopOwnerData();
    } catch (error) {
      console.error("Delete material error:", error);
      alert(error.message || "Failed to delete material.");
    }
  };

  const handleUploadCatalogue = async () => {
    try {
      if (!shop) return;
      if (!catalogueFile) {
        alert("Please select a catalogue file.");
        return;
      }

      const catalogueFileName = `${shop.id}/catalogues/${Date.now()}-${catalogueFile.name}`;

      const { error: uploadError } = await supabase.storage
        .from("shop-assets")
        .upload(catalogueFileName, catalogueFile, {
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase.from("shop_catalogues").insert([
        {
          shop_id: shop.id,
          file_name: catalogueFile.name,
          file_path: catalogueFileName,
          status: "uploaded",
        },
      ]);

      if (dbError) throw dbError;

      setCatalogueFile(null);
      alert("Catalogue uploaded successfully.");
      fetchShopOwnerData();
    } catch (error) {
      console.error("Catalogue upload error:", error);
      alert(error.message || "Failed to upload catalogue.");
    }
  };

  const handleMaterialOrderStatusUpdate = async (orderId, status) => {
    try {
      const { error } = await supabase
        .from("material_orders")
        .update({ delivery_status: status })
        .eq("id", orderId);

      if (error) throw error;

      setMaterialOrders((prev) =>
        prev.map((item) =>
          item.id === orderId ? { ...item, delivery_status: status } : item
        )
      );
    } catch (error) {
      console.error("Material order status update error:", error);
      alert(error.message || "Failed to update material order status.");
    }
  };

  if (authLoading || loading) {
    return <div style={page} className="shop-owner-dashboard"><p>Loading shop owner dashboard...</p></div>;
  }

  if (!user || profile?.role !== "shop_owner") {
    return <div style={page} className="shop-owner-dashboard"><p>Access denied.</p></div>;
  }

  const fulfillmentReadinessGaps = [];

  if (!shopOwner?.is_active) {
    fulfillmentReadinessGaps.push("inactive");
  }

  if (!shopOwner?.live_city_allowed) {
    fulfillmentReadinessGaps.push("city hold");
  }

  if (!shopForm.business_name) {
    fulfillmentReadinessGaps.push("missing business name");
  }

  if (!profile?.phone) {
    fulfillmentReadinessGaps.push("missing phone");
  }

  if (!shopForm.city) {
    fulfillmentReadinessGaps.push("missing city");
  }

  if (!shopForm.gst_number) {
    fulfillmentReadinessGaps.push("missing GST");
  }

  return (
    <div style={page} className="shop-owner-dashboard">
      <div style={heroCard} className="shop-owner-dashboard__hero">
        <h1 style={{ marginTop: 0 }}>Shop Owner Dashboard</h1>
        <p style={mutedBig}>
          Manage your shop identity, GST, address, catalogue, wallet, referral, and inventory.
        </p>
      </div>

      <div style={summaryGrid} className="shop-owner-dashboard__summary-grid">
        <div style={summaryCard}>
          <div style={summaryLabel}>Wallet Balance</div>
          <div style={summaryValue}>
            ₹{Number(shopOwner?.wallet_balance || 0).toLocaleString()}
          </div>
          <div style={summarySubtext}>Weekly release cycle: Saturday</div>
        </div>

        <div style={summaryCard}>
          <div style={summaryLabel}>Completed Orders</div>
          <div style={summaryValue}>
            {shopOwner?.completed_orders_count || 0}
          </div>
          <div style={summarySubtext}>Keep quality stable for retention</div>
        </div>

        <div style={summaryCard}>
          <div style={summaryLabel}>Referral Bonus Earned</div>
          <div style={summaryValue}>
            ₹{Number(shopOwner?.referral_bonus_earned || 0).toLocaleString()}
          </div>
          <div style={summarySubtext}>Dynamic and margin-protected</div>
        </div>

        <div style={summaryCard}>
          <div style={summaryLabel}>Referral Code</div>
          <div style={summaryValue}>
            {shopOwner?.referral_code || "Not set yet"}
          </div>
          <div style={summarySubtext}>Use for partner onboarding</div>
        </div>
      </div>

      <div style={card} className="shop-owner-dashboard__card">
        <h2>My Profile</h2>
        <div style={profileGrid} className="shop-owner-dashboard__profile-grid">
          <div style={profileBox}>
            <div style={profileLabel}>Full Name</div>
            <div style={profileValue}>{profile?.full_name || "N/A"}</div>
          </div>
          <div style={profileBox}>
            <div style={profileLabel}>Phone</div>
            <div style={profileValue}>{profile?.phone || "N/A"}</div>
          </div>
          <div style={profileBox}>
            <div style={profileLabel}>Email</div>
            <div style={profileValue}>{profile?.email || "N/A"}</div>
          </div>
          <div style={profileBox}>
            <div style={profileLabel}>Unique ID</div>
            <div style={profileValue}>{profile?.display_user_id || "N/A"}</div>
          </div>
        </div>
      </div>

      <div style={card} className="shop-owner-dashboard__card">
        <h2>Shop Details</h2>

        <div
          style={{
            ...readinessBox,
            borderColor:
              fulfillmentReadinessGaps.length === 0 ? "#28593a" : "#5a4a16",
            background:
              fulfillmentReadinessGaps.length === 0 ? "#102516" : "#191408",
          }}
        >
          <strong style={{ display: "block", marginBottom: "8px" }}>
            Fulfillment Readiness
          </strong>
          <p style={{ ...muted, marginTop: 0 }}>
            {fulfillmentReadinessGaps.length === 0
              ? "This shop profile looks ready for live material fulfillment."
              : `Live fulfillment may not reach this shop yet. Missing: ${fulfillmentReadinessGaps.join(
                  ", "
                )}.`}
          </p>
        </div>

        <input
          name="business_name"
          placeholder="Business Name"
          value={shopForm.business_name}
          onChange={handleShopChange}
          style={input}
        />

        <input
          name="shop_name"
          placeholder="Shop Name"
          value={shopForm.shop_name}
          onChange={handleShopChange}
          style={input}
        />

        <input
          name="gst_number"
          placeholder="GST Number"
          value={shopForm.gst_number}
          onChange={handleShopChange}
          style={input}
        />

        <input
          name="flat_house_no"
          placeholder="Flat No / House No"
          value={shopForm.flat_house_no}
          onChange={handleShopChange}
          style={input}
        />

        <input
          name="street_area"
          placeholder="Street / Area Name"
          value={shopForm.street_area}
          onChange={handleShopChange}
          style={input}
        />

        <textarea
          name="address"
          placeholder="Full Address"
          value={shopForm.address}
          onChange={handleShopChange}
          style={textarea}
        />

        <div style={twoColGrid} className="shop-owner-dashboard__two-col-grid">
          <input
            name="city"
            placeholder="City"
            value={shopForm.city}
            onChange={handleShopChange}
            style={input}
          />

          <input
            name="state"
            placeholder="State"
            value={shopForm.state}
            onChange={handleShopChange}
            style={input}
          />

          <input
            name="country"
            placeholder="Country"
            value={shopForm.country}
            onChange={handleShopChange}
            style={input}
          />

          <input
            name="pincode"
            placeholder="Pincode"
            value={shopForm.pincode}
            onChange={handleShopChange}
            style={input}
          />
        </div>

        <button onClick={handleSaveShop} style={button}>
          Save Shop Details
        </button>
      </div>

      <div style={card} className="shop-owner-dashboard__card">
        <h2>Add Material</h2>

        <div style={skuDirectoryBox}>
          <h3 style={{ marginTop: 0 }}>Standard SKU Directory</h3>
          <p style={muted}>
            Inventory should be mapped to a platform-owned master SKU. This keeps
            the same market item standardized across all shop owners and prevents
            duplicate SKU identities for fixed items like 8mm screws, switches,
            tiles, or pipes.
          </p>

          <input
            placeholder="Search screw, 12mm tmt, pvc pipe 25mm, basin..."
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value)}
            style={input}
          />

          <div style={categoryHint}>
            {skuSearch.trim()
              ? "Searching live master SKU catalog in Supabase."
              : `Starter coverage: ${starterSkuCategories.join(", ")}`}
          </div>

          {remoteSkuLoading ? <div style={categoryHint}>Searching SKU catalog...</div> : null}
          {remoteSkuError ? <div style={categoryHint}>{remoteSkuError}</div> : null}

          <div style={skuList} className="shop-owner-dashboard__sku-list">
            {displayedSkuResults.slice(0, 12).map((item) => (
              <button
                key={item.skuCode}
                type="button"
                onClick={() => applyMasterSku(item.skuCode)}
                style={{
                  ...skuChip,
                  background:
                    materialForm.master_sku_code === item.skuCode ? "#fff" : "#111",
                  color:
                    materialForm.master_sku_code === item.skuCode ? "#000" : "#fff",
                }}
              >
                {item.skuCode} | {item.title}
              </button>
            ))}
          </div>
        </div>

        <input
          name="master_sku_id"
          placeholder="Selected master SKU id"
          value={materialForm.master_sku_id}
          onChange={handleMaterialChange}
          style={input}
          disabled
        />

        <input
          name="master_sku_code"
          placeholder="Selected master SKU"
          value={materialForm.master_sku_code}
          onChange={handleMaterialChange}
          style={input}
          disabled
        />

        <input
          name="material_name"
          placeholder="Material Name"
          value={materialForm.material_name}
          onChange={handleMaterialChange}
          style={input}
          disabled
        />

        <div style={twoColGrid} className="shop-owner-dashboard__two-col-grid">
          <select
            name="category"
            value={materialForm.category}
            onChange={handleMaterialChange}
            style={input}
            disabled
          >
            {MATERIAL_CATEGORY_OPTIONS.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <input
            name="unit"
            placeholder="Unit"
            value={materialForm.unit}
            onChange={handleMaterialChange}
            style={input}
            disabled
          />

          <input
            name="quantity"
            placeholder="Quantity"
            value={materialForm.quantity}
            onChange={handleMaterialChange}
            style={input}
          />

          <input
            name="mrp_price"
            placeholder="MRP"
            value={materialForm.mrp_price}
            onChange={handleMaterialChange}
            style={input}
          />

          <input
            name="offered_price"
            placeholder="Offered Price"
            value={materialForm.offered_price}
            onChange={handleMaterialChange}
            style={input}
          />
        </div>

        <input
          name="image_url"
          placeholder="Image URL (optional)"
          value={materialForm.image_url}
          onChange={handleMaterialChange}
          style={input}
        />

        <label style={label}>Upload Material Image</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleMaterialImageChange}
          style={fileInput}
        />

        <button onClick={handleAddMaterial} style={button}>
          Add Material
        </button>

        <p style={{ ...muted, marginBottom: 0, marginTop: "12px" }}>
          Workflow: search the market SKU, select it once, then enter only your
          available quantity, MRP, and offered price for that fixed item.
        </p>
        <p style={{ ...muted, marginBottom: 0, marginTop: "12px" }}>
          Better seller pricing improves how attractive your marketplace listing
          becomes. Customers only see MistriBazaar marketplace prices, not your
          private offered price.
        </p>
      </div>

      <div style={card} className="shop-owner-dashboard__card">
        <h2>Upload Catalogue</h2>
        <p style={muted}>
          Upload PDF, Excel, CSV, or image catalogue. Inventory can be prepared from it later.
        </p>

        <input
          type="file"
          accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png"
          onChange={handleCatalogueChange}
          style={fileInput}
        />

        <button onClick={handleUploadCatalogue} style={button}>
          Upload Catalogue
        </button>

        {catalogues.length > 0 && (
          <div style={{ marginTop: "18px" }}>
            <h3>Uploaded Catalogues</h3>
            {catalogues.map((item) => (
              <div key={item.id} style={innerCard}>
                <p><strong>File:</strong> {item.file_name}</p>
                <p><strong>Status:</strong> {item.status}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={card} className="shop-owner-dashboard__card">
        <h2>Incoming Material Orders</h2>

        {materialOrders.length === 0 ? (
          <p style={muted}>No material orders have reached this shop yet.</p>
        ) : (
          <div style={{ display: "grid", gap: "14px" }}>
            {materialOrders.map((order) => (
              <div key={order.id} style={innerCard}>
                <h3 style={{ marginTop: 0 }}>{order.material_title || "Material Order"}</h3>
                <p><strong>Order ID:</strong> {order.orderCode}</p>
                <p><strong>Quantity:</strong> {order.quantity} {order.unit || "unit"}</p>
                <p><strong>Amount:</strong> ₹{order.final_price || 0}</p>
                <p>
                  <strong>Delivery Estimate:</strong>{" "}
                  {order.delivery_estimate || "Awaiting estimate"}
                </p>
                <p>
                  <strong>Delivery Address:</strong>{" "}
                  {order.deliveryAddressText || "Address not available yet"}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {String(order.delivery_status || "awaiting_shop_confirmation")
                    .replaceAll("_", " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                </p>
                <p>
                  <strong>Delivery Partner:</strong>{" "}
                  {order.assigned_delivery_partner || "Not assigned yet"}
                </p>
                <p>
                  <strong>Tracking ID:</strong> {order.tracking_id || "Not generated"}
                </p>
                <p>
                  <strong>Priority Delivery:</strong>{" "}
                  {order.priority_delivery ? "Yes" : "No"}
                </p>

                <div style={actionRow} className="shop-owner-dashboard__action-row">
                  <button
                    type="button"
                    onClick={() =>
                      handleMaterialOrderStatusUpdate(
                        order.id,
                        "shop_confirmed"
                      )
                    }
                    style={button}
                  >
                    Confirm Order
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleMaterialOrderStatusUpdate(
                        order.id,
                        "preparing_dispatch"
                      )
                    }
                    style={secondaryBtn}
                    >
                      Mark Preparing
                    </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleMaterialOrderStatusUpdate(
                        order.id,
                        "out_for_delivery"
                      )
                    }
                    style={secondaryBtn}
                  >
                    Out for Delivery
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleMaterialOrderStatusUpdate(order.id, "delivered")
                    }
                    style={button}
                  >
                    Mark Delivered
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={card} className="shop-owner-dashboard__card">
        <h2>My Inventory</h2>

        {materials.length === 0 ? (
          <p>No materials added yet.</p>
        ) : (
          materials.map((item) => (
            <div key={item.id} style={innerCard}>
              <div style={{ display: "flex", gap: "18px", flexWrap: "wrap" }}>
                <div style={imageWrap}>
                  {item.resolvedImageUrl ? (
                    <img
                      src={item.resolvedImageUrl}
                      alt={item.material_name}
                      style={image}
                    />
                  ) : (
                    <div style={imagePlaceholder}>No Image</div>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: "240px" }}>
                  <h3>{item.material_name}</h3>
                  <p><strong>Category:</strong> {item.category}</p>
                  <p><strong>Quantity:</strong> {item.quantity}</p>
                  <p><strong>Unit:</strong> {item.unit}</p>
                  <p><strong>Seller MRP:</strong> ₹{item.mrp_price ?? item.price}</p>
                  <p><strong>Seller Offered Price:</strong> ₹{item.offered_price ?? item.price}</p>
                  <p><strong>Marketplace MRP:</strong> ₹{item.customer_mrp_price ?? item.mrp_price ?? item.price}</p>
                  <p><strong>Marketplace Offered Price:</strong> ₹{item.customer_offer_price ?? item.offered_price ?? item.price}</p>

                  <button
                    onClick={() => handleDeleteMaterial(item.id)}
                    style={dangerBtn}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div style={card} className="shop-owner-dashboard__card">
        <h2>Wallet Rules</h2>
        <ul style={list}>
          <li>Earnings are collected in wallet and released weekly on Saturday.</li>
          <li>Cash orders can still deduct service/platform fees from wallet.</li>
          <li>Wallet can temporarily go negative if needed.</li>
          <li>This improves dispute handling, replacements, and cash-flow control.</li>
        </ul>
      </div>

      <div style={card} className="shop-owner-dashboard__card">
        <h2>Referral Program</h2>
        <p style={muted}>
          Similar to workers, shop owners can also participate in referral-led retention and acquisition flows, but bonuses should remain margin-protected and not burn cash.
        </p>
      </div>
    </div>
  );
}

const page = {
  background: "#000",
  minHeight: "100vh",
  padding: "40px",
  color: "#fff",
  fontFamily: "Arial, sans-serif",
  maxWidth: "1100px",
  margin: "0 auto",
};

const heroCard = {
  background: "linear-gradient(135deg, #111, #191919)",
  padding: "24px",
  borderRadius: "18px",
  border: "1px solid #2a2a2a",
  marginBottom: "20px",
};

const card = {
  background: "#111",
  padding: "22px",
  borderRadius: "16px",
  border: "1px solid #2a2a2a",
  marginBottom: "20px",
};

const innerCard = {
  background: "#0b0b0b",
  padding: "18px",
  borderRadius: "14px",
  border: "1px solid #222",
  marginBottom: "14px",
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px",
  marginBottom: "20px",
};

const summaryCard = {
  background: "#111",
  border: "1px solid #2a2a2a",
  borderRadius: "16px",
  padding: "18px",
};

const summaryLabel = {
  color: "#9a9a9a",
  fontSize: "13px",
  marginBottom: "8px",
};

const summaryValue = {
  color: "#fff",
  fontWeight: "bold",
  fontSize: "24px",
};

const summarySubtext = {
  color: "#bdbdbd",
  fontSize: "12px",
  marginTop: "8px",
};

const profileGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px",
  marginTop: "12px",
};

const profileBox = {
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "12px",
  padding: "16px",
};

const profileLabel = {
  color: "#aaa",
  fontSize: "13px",
  marginBottom: "6px",
};

const profileValue = {
  color: "#fff",
  fontWeight: "bold",
  wordBreak: "break-word",
};

const input = {
  width: "100%",
  padding: "12px 14px",
  marginBottom: "12px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  boxSizing: "border-box",
};

const readinessBox = {
  border: "1px solid #2a2a2a",
  borderRadius: "12px",
  padding: "14px 16px",
  marginBottom: "16px",
};

const textarea = {
  width: "100%",
  padding: "12px 14px",
  marginBottom: "12px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  boxSizing: "border-box",
  minHeight: "90px",
};

const skuDirectoryBox = {
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "14px",
  padding: "16px",
  marginBottom: "16px",
};

const categoryHint = {
  color: "#aaa",
  fontSize: "13px",
  marginBottom: "12px",
};

const skuList = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const skuChip = {
  padding: "10px 12px",
  border: "1px solid #2a2a2a",
  borderRadius: "999px",
  cursor: "pointer",
};

const label = {
  display: "block",
  color: "#fff",
  marginBottom: "8px",
};

const fileInput = {
  width: "100%",
  padding: "12px 14px",
  marginBottom: "12px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  boxSizing: "border-box",
};

const button = {
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const secondaryBtn = {
  padding: "12px 18px",
  border: "1px solid #fff",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const actionRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginTop: "14px",
};

const dangerBtn = {
  padding: "10px 16px",
  border: "1px solid #ff4d4f",
  background: "#111",
  color: "#ff4d4f",
  borderRadius: "10px",
  cursor: "pointer",
};

const imageWrap = {
  width: "180px",
  height: "140px",
  borderRadius: "12px",
  overflow: "hidden",
  background: "#111",
  border: "1px solid #222",
};

const image = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const imagePlaceholder = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#777",
  fontWeight: "bold",
};

const twoColGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "12px",
};

const muted = {
  color: "#d1d1d1",
  lineHeight: "1.7",
};

const mutedBig = {
  color: "#d1d1d1",
  lineHeight: "1.7",
  marginBottom: 0,
};

const list = {
  color: "#d1d1d1",
  lineHeight: "1.8",
  paddingLeft: "20px",
};

export default ShopOwnerDashboard;
